import { useEffect, useMemo, useReducer, useRef } from 'react'
import { pinnedBatch, type PinnedBatch } from '@papan/api-client'

/**
 * A form-level Save/Discard controller for a screen made of several independent
 * SECTIONS — each with its own draft state, endpoint and validation.
 *
 * The price book's plan Edit dialog and the platform console's per-tenant Plan
 * tab both present the same handful of controls (grants, limits, model access,
 * pricing). Rather than a Save bar per section, each section REGISTERS its dirty
 * count and its `save` / `discard` here, and the host shows ONE bar that fans
 * out. `bar` is passed to each section as an OPTIONAL prop, so a section rendered
 * WITHOUT it keeps its own bar — the unification is opt-in and backward-compatible.
 *
 * CRITICAL — a section's `save` must not, in bar mode, trigger a reload that
 * re-seeds a SIBLING section's still-unsaved draft. The host orders the fan-out
 * so the vulnerable section (typically the one whose draft has no content-key
 * re-seed guard) is written first. See `saveAll`.
 */
/** A section's save. `batch` (#1316 review round 2, owner 2026-10-28): the ONE Save the person pressed — every
 *  section's writes are sent as the identity that pressed it (`batch.run` / `batch.opts()`), so a tenant / identity
 *  switch mid-Save FINISHES the Save as its starter instead of writing the rest as the next session; a section shows
 *  nothing (no toast, no reload) once `batch.ended()`. Absent (a section saved on its own): the section takes its own. */
export type SectionSave = (batch?: PinnedBatch) => Promise<void>

export interface PlanEditBar {
  register: (key: string, entry: { count: number; save: SectionSave; discard: () => void }) => void
  unregister: (key: string) => void
}

/**
 * Create a controller. `total` is the summed dirty count across sections;
 * `saveAll` / `discardAll` fan out. `firstKeys` names sections that must be
 * saved BEFORE the rest (e.g. a scalar-fields section whose draft a sibling's
 * reload would clobber), in the given order.
 */
export function usePlanEditBar(firstKeys: string[] = []) {
  const sections = useRef(new Map<string, { count: number; save: SectionSave; discard: () => void }>())
  const [, bump] = useReducer((x: number) => x + 1, 0)
  const bar = useMemo<PlanEditBar>(() => ({
    register: (key, entry) => {
      const prev = sections.current.get(key)
      sections.current.set(key, entry)
      // Only a changed COUNT needs a re-render (for the bar's total); the
      // save/discard closures are read live at save time, so silently updating
      // them avoids a render loop.
      if (!prev || prev.count !== entry.count) bump()
    },
    unregister: (key) => { if (sections.current.delete(key)) bump() },
  }), [])
  const total = [...sections.current.values()].reduce((n, s) => n + s.count, 0)
  const saveAll = async (batch: PinnedBatch = pinnedBatch()) => {
    // Only DIRTY sections are written — a non-dirty section's save would at best
    // reload for nothing and at worst re-write an unchanged value.
    const entries = [...sections.current.entries()].filter(([, s]) => s.count > 0)
    const first = firstKeys
      .map((k) => entries.find(([ek]) => ek === k))
      .filter((e): e is [string, { count: number; save: SectionSave; discard: () => void }] => !!e)
    const rest = entries.filter(([k]) => !firstKeys.includes(k))
    // #1316 review round 2: ONE batch for the whole Save — every section's writes go as the identity that pressed it
    for (const [, s] of [...first, ...rest]) await s.save(batch)
  }
  const discardAll = () => { for (const s of [...sections.current.values()]) s.discard() }
  return { bar, total, saveAll, discardAll }
}

/**
 * Register one section's live count/save/discard into a host's bar.
 * `save`/`discard` are read through refs so the stored closure is never stale,
 * while the registration effect re-runs only when the COUNT changes.
 */
export function useRegisterSection(
  bar: PlanEditBar | undefined,
  key: string,
  count: number,
  save: SectionSave,
  discard: () => void,
) {
  const saveRef = useRef(save); saveRef.current = save
  const discardRef = useRef(discard); discardRef.current = discard
  useEffect(() => {
    if (!bar) return
    bar.register(key, { count, save: (batch) => saveRef.current(batch), discard: () => discardRef.current() })
    return () => bar.unregister(key)
  }, [bar, key, count])
}
